import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { contaCollegati, eliminaCommessaCompleta } from "../utils/eliminaCommessa";
import { useToast } from "../utils/Toast";
import { messaggioErrore } from "../utils/erroriUtente";
import { AlertTriangle, X } from "lucide-react";

export default function DeleteProjectModal({ project, onClose, onDeleted }) {
  const { addToast } = useToast();
  const { currentUser, userProfile } = useAuth();
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [collegati, setCollegati] = useState(null); // null = conteggio in corso
  const cleanName = (project.name||"").trim().replace(/\s+/g," ");
  // W423: il confronto non dipende da maiuscole, accenti, apostrofi curvi (’), spazi doppi o
  // invisibili e punteggiatura: prima, con un nome copiato da un documento, il tasto restava
  // spento anche scrivendo il nome giusto. Vale anche il codice commessa (es. 31-26).
  const normalizza = (t) => String(t||"").normalize("NFKD").replace(/[\u0300-\u036f]/g,"")
    .replace(/[^a-zA-Z0-9]+/g,"").toLowerCase();
  const scritto = normalizza(confirmText);
  const matches = scritto.length > 0 && (scritto === normalizza(cleanName) ||
    (!!project.codiceCommessa && scritto === normalizza(project.codiceCommessa)));

  // W422: prima di confermare, cosa sparirà insieme alla commessa
  useEffect(() => {
    let vivo = true;
    contaCollegati(project).then(r => { if (vivo) setCollegati(r); }).catch(() => { if (vivo) setCollegati({ voci: [], numerati: [] }); });
    return () => { vivo = false; };
  }, [project]);

  const handleDelete = async () => {
    if (!matches) return;
    setDeleting(true);
    try {
      const esito = await eliminaCommessaCompleta(project, { uid: currentUser?.uid || "", nome: userProfile?.displayName || currentUser?.displayName || "" });
      if (esito.ancoraPresente) {
        addToast("La commessa risulta ancora nel database: l'eliminazione non è stata accettata. Riprova o scrivi all'assistenza.", "error", 10000);
        setDeleting(false);
        return;
      }
      addToast(esito.nonEliminati.length
        ? `Commessa eliminata. Non è stato possibile eliminare: ${esito.nonEliminati.join(", ")}.`
        : "Commessa eliminata definitivamente, con tutti i documenti collegati", esito.nonEliminati.length ? "info" : "success", esito.nonEliminati.length ? 10000 : 4000);
      onDeleted();
    } catch (err) {
      addToast(messaggioErrore(err, "eliminare la commessa"), "error");
      setDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && !deleting && onClose()}>
      <div className="modal" style={{maxWidth:440}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:12,marginBottom:16}}>
          <div style={{width:40,height:40,borderRadius:10,background:"#fee2e2",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <AlertTriangle size={20} color="#dc2626"/>
          </div>
          <div style={{flex:1}}>
            <h3 style={{fontSize:16,fontWeight:700,color:"#111827"}}>Elimina definitivamente</h3>
            <p style={{fontSize:13,color:"#6b7280",marginTop:2}}>Questa azione non può essere annullata</p>
          </div>
          <button className="btn-icon" onClick={onClose} disabled={deleting}><X size={16}/></button>
        </div>

        <div style={{background:"#fff7ed",border:"1px solid #fed7aa",borderRadius:10,padding:"12px 14px",marginBottom:16,fontSize:13,color:"#92400e",lineHeight:1.5}}>
          Stai per eliminare definitivamente la commessa <strong>"{cleanName}"</strong> con tutto quello che le appartiene: attività e sezioni, e tutti i documenti qui sotto. Il cliente, le richieste commerciali e le offerte restano (scollegati). L'eliminazione resta scritta nel Log Modifiche.
        </div>
        <div style={{fontSize:12.5,color:"#374151",marginBottom:14,lineHeight:1.5}}>
          {collegati === null ? <span style={{color:"#9ca3af"}}>Controllo dei documenti collegati…</span>
            : collegati.voci.length === 0 ? <span>Nessun altro documento collegato.</span>
            : <><b>Verranno eliminati anche:</b> {collegati.voci.map(v => v.testo).join(", ")}.</>}
          {collegati?.numerati?.length > 0 && (
            <div role="alert" style={{marginTop:8,background:"#fef2f2",border:"1px solid #fca5a5",borderRadius:8,padding:"8px 12px",color:"#b91c1c"}}>
              <b>Attenzione, documenti numerati o fatturati:</b> {collegati.numerati.join(", ")}. Spariranno anche dai registri e la loro numerazione resterà con dei buchi. Se la commessa è vera, è meglio archiviarla.
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">
            Per confermare, scrivi il nome della commessa: <strong>{cleanName}</strong>{project.codiceCommessa ? <> (oppure il codice <strong>{project.codiceCommessa}</strong>)</> : null}
          </label>
          <input className="input" value={confirmText} onChange={e=>setConfirmText(e.target.value)}
            placeholder={cleanName} autoFocus/>
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
          <button className="btn btn-secondary" onClick={onClose} disabled={deleting}>Annulla</button>
          <button onClick={handleDelete} disabled={!matches || deleting}
            style={{padding:"8px 16px",background: matches?"#dc2626":"#fca5a5",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:600,cursor: matches?"pointer":"not-allowed",fontFamily:"'Inter',sans-serif"}}>
            {deleting ? "Eliminazione in corso..." : "Elimina definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}
